/**
 * Capability 6: Platform
 * Purpose: Configures and manages the Altara platform.
 */
import { useEffect, useState } from "react";
import CapabilityLandingPage from "@/components/capability/CapabilityLandingPage";
import api from "@/lib/api";
import {
  Building2, Users, ShieldCheck, KeyRound, Plug, Palette, ToggleLeft, ScrollText, Activity,
} from "lucide-react";

export default function PlatformCapability() {
  const [kpis, setKpis] = useState({ tenants: null, users: null, keys: null });

  useEffect(() => {
    (async () => {
      const next = { tenants: null, users: null, keys: null };
      try { const { data } = await api.get("/admin/tenants"); next.tenants = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/admin/users"); next.users = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      // API keys — leave placeholder unless dedicated endpoint exists
      setKpis(next);
    })();
  }, []);

  return (
    <CapabilityLandingPage
      eyebrow="Capability 6 · Platform"
      title="Platform"
      purpose="Configures and manages the Altara platform. Tenants, users, roles, providers, branding and feature flags — the substrate on which every other capability runs."
      primaryActions={[
        { label: "Manage Tenants", to: "/institutions", icon: Building2 },
        { label: "Browse Providers", to: "/platform/providers", icon: Plug },
      ]}
      kpis={[
        { label: "Tenants", value: kpis.tenants, subLabel: "total" },
        { label: "Users", value: kpis.users, subLabel: "with active roles" },
        { label: "API keys", value: null, emptyHint: "Wire on /admin/api-keys/count" },
        { label: "Uptime", value: null, emptyHint: "Health probe pending" },
      ]}
      subCapabilities={[
        { name: "Tenants", to: "/institutions", icon: Building2, status: "live",
          description: "Full lifecycle of institution tenants — onboard, configure, suspend." },
        { name: "Users", to: "/administration", icon: Users, status: "live",
          description: "Every user, their tenant, their roles, active sessions." },
        { name: "Roles", to: "/administration", icon: ShieldCheck, status: "live",
          description: "12 RBAC roles from platform_administrator to read_only." },
        { name: "API Keys", to: "/administration", icon: KeyRound, status: "live",
          description: "Banker + institution API keys. Mint, rotate, revoke." },
        { name: "Providers", to: "/platform/providers", icon: Plug, status: "live",
          description: "Integration marketplace — KYC, Identity, Biometrics, Credit, Payments, AI…" },
        { name: "Trust Utility", to: "/platform/trust-utility", icon: ShieldCheck, status: "live",
          description: "Provider-neutral orchestration layer. Every external verification routes through here." },
        { name: "Branding", to: "/platform/branding", icon: Palette, status: "future",
          description: "Per-tenant colour, logo, email templates." },
        { name: "Feature Flags", to: "/platform/feature-flags", icon: ToggleLeft, status: "future",
          description: "Per-tenant progressive rollout — gate risky features." },
        { name: "Audit Logs", to: "/administration", icon: ScrollText, status: "live",
          description: "Platform-wide audit log — signed, tamper-evident." },
        { name: "Platform Health", to: "/platform/health", icon: Activity, status: "placeholder",
          description: "Live service health, event bus throughput, storage utilisation." },
      ]}
      relatedCapabilities={[
        { name: "Governance", to: "/capability/governance" },
        { name: "Institution Workspace", to: "/capability/institution-workspace" },
      ]}
    />
  );
}
